import React, { useState, useEffect, ReactNode, createContext } from 'react';
import { format, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { TIMEZONE_OPTIONS } from './timezoneOptions';

const STORAGE_KEY = 'jua-user-timezone';

interface TimezoneProviderProps {
  children: ReactNode;
}

export interface TimezoneContextType {
  timezone: string;
  setTimezone: (tz: string) => void;
  convertToUserTimezone: (utcDate: Date | string) => Date;
  formatInUserTimezone: (utcDate: Date | string, formatStr: string) => string;
  convertToUTC: (userDate: Date, userTimezone?: string) => Date;
}

export const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  // Initialize with user's browser timezone or UTC as fallback
  const getDefaultTimezone = (): string => {
    try {
      // Try to get saved timezone from localStorage
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && TIMEZONE_OPTIONS.some((tz) => tz.value === saved)) {
        return saved;
      }

      // Try to get browser timezone
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (TIMEZONE_OPTIONS.some((tz) => tz.value === browserTz)) {
        return browserTz;
      }

      // Default to UTC
      return 'UTC';
    } catch {
      return 'UTC';
    }
  };

  const [timezone, setTimezoneState] = useState<string>(getDefaultTimezone);

  useEffect(() => {
    // Save timezone to localStorage when it changes
    try {
      localStorage.setItem(STORAGE_KEY, timezone);
    } catch (error) {
      console.warn('Failed to save timezone to localStorage:', error);
    }
  }, [timezone]);

  const setTimezone = (tz: string) => {
    if (TIMEZONE_OPTIONS.some((option) => option.value === tz)) {
      setTimezoneState(tz);
    }
  };

  const convertToUserTimezone = (utcDate: Date | string): Date => {
    try {
      const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
      if (timezone === 'UTC') {
        return date;
      }
      return toZonedTime(date, timezone);
    } catch (error) {
      console.warn('Failed to convert date to user timezone:', error);
      return typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    }
  };

  const formatInUserTimezone = (utcDate: Date | string, formatStr: string): string => {
    try {
      const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
      if (timezone === 'UTC') {
        // For UTC, use standard formatting
        return format(date, formatStr, { timeZone: 'UTC' });
      } else {
        // For other timezones, convert first then format
        const zonedDate = toZonedTime(date, timezone);
        return format(zonedDate, formatStr, { timeZone: timezone });
      }
    } catch (error) {
      console.warn('Failed to format date in user timezone:', error);
      const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
      return format(date, formatStr);
    }
  };

  const convertToUTC = (userDate: Date, userTimezone?: string): Date => {
    try {
      const tz = userTimezone || timezone;
      if (tz === 'UTC') {
        return userDate;
      }
      return fromZonedTime(userDate, tz);
    } catch (error) {
      console.warn('Failed to convert date to UTC:', error);
      return userDate;
    }
  };

  const value: TimezoneContextType = {
    timezone,
    setTimezone,
    convertToUserTimezone,
    formatInUserTimezone,
    convertToUTC,
  };

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};
